package com.arena_web.ui.components.feed

import androidx.compose.runtime.*
import com.arena_web.ui.theme.AppIcons
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.styleModifier
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.events.Event

// Instagram-red heart (#ED4956) for the double-tap burst.
private const val BURST_RED = "#ED4956"

/**
 * Wraps a post's image content and adds Instagram-style double-tap-to-like: a
 * double-tap fires [onDoubleTapLike] and replays a red heart-burst over the
 * image. The heart-burst always plays on a double-tap; whether the like is
 * actually applied (and never un-applied) is the caller's concern — the backend
 * `likePost` is a toggle, so callers should make [onDoubleTapLike] idempotent
 * (like-only).
 *
 * Detection is a native `pointerdown`/`pointerup` double-tap bound once via
 * `ref`: it works for both mouse and touch, a movement guard ignores scrolls,
 * and — crucially — the tap-pairing state lives in the listener closure rather
 * than in Compose state, so the feed list's frequent recompositions can't drop
 * the pairing (the reason hand-paired `click`/`dblclick` worked in the static
 * comment sheet but not in the list). `touch-action: manipulation` keeps the
 * mobile double-tap from zooming. The latest [onDoubleTapLike] is read through a
 * ref so the once-bound listener never calls a stale callback.
 *
 * Burst size is a raw pixel value so it renders identically inside the
 * `--scale`-wrapped comment sheet and the unscaled feed list.
 *
 * [onImageTap] (optional) adds single-tap handling: a tap on an image tile
 * reports the tile's index (photo-viewer open). It fires after the 350ms
 * double-tap window so a double-tap only likes and never also opens the
 * viewer. The tile index is resolved from the DOM — grid tiles are the divs
 * with an inline `background-image`, rendered in url order — so every grid
 * variant (desktop, mobile, collage) works without changes.
 */
@Composable
fun DoubleTapLikeArea(
    onDoubleTapLike: () -> Unit,
    modifier: Modifier = Modifier,
    heartSizePx: Int = 88,
    onImageTap: ((Int) -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    // Stable holders so the once-bound listener mutates live state and always
    // calls the freshest callback.
    val burstKey = remember { mutableStateOf(0) }
    val latestCb = remember { mutableStateOf(onDoubleTapLike) }
    latestCb.value = onDoubleTapLike
    val latestTapCb = remember { mutableStateOf(onImageTap) }
    latestTapCb.value = onImageTap

    Box(
        modifier = modifier
            .styleModifier {
                property("position", "relative")
                property("cursor", "pointer")
                // Keep a mobile double-tap from triggering zoom.
                property("touch-action", "manipulation")
                // Don't let a double-tap select the image/text underneath.
                property("user-select", "none")
                property("-webkit-user-select", "none")
            }
            .attrsModifier {
                ref { el ->
                    // Closure-local (not Compose) state → survives recomposition.
                    var lastTapMs = 0.0
                    var downX = 0.0
                    var downY = 0.0
                    var pendingTap = -1   // window.setTimeout id of a delayed single tap
                    val onDown: (Event) -> Unit = { e ->
                        val pe = e.asDynamic()
                        downX = (pe.clientX as Number).toDouble()
                        downY = (pe.clientY as Number).toDouble()
                    }
                    val onUp: (Event) -> Unit = { e ->
                        val pe = e.asDynamic()
                        val ux = (pe.clientX as Number).toDouble()
                        val uy = (pe.clientY as Number).toDouble()
                        // Ignore scroll/drag releases — only true taps count.
                        val moved = kotlin.math.abs(ux - downX) + kotlin.math.abs(uy - downY)
                        if (moved < 16.0) {
                            val now = kotlin.js.Date().getTime()
                            if (now - lastTapMs < 350.0) {
                                // Double tap — cancel the pending single tap, like.
                                if (pendingTap >= 0) {
                                    window.clearTimeout(pendingTap)
                                    pendingTap = -1
                                }
                                burstKey.value += 1   // replay the heart pop
                                latestCb.value()
                                lastTapMs = 0.0
                            } else {
                                lastTapMs = now
                                if (latestTapCb.value != null) {
                                    val target = e.target as? Element
                                    if (pendingTap >= 0) window.clearTimeout(pendingTap)
                                    pendingTap = window.setTimeout({
                                        pendingTap = -1
                                        resolveTileIndex(el, target)
                                            ?.let { idx -> latestTapCb.value?.invoke(idx) }
                                    }, 350)
                                }
                            }
                        }
                    }
                    el.addEventListener("pointerdown", onDown)
                    el.addEventListener("pointerup", onUp)
                    onDispose {
                        if (pendingTap >= 0) window.clearTimeout(pendingTap)
                        el.removeEventListener("pointerdown", onDown)
                        el.removeEventListener("pointerup", onUp)
                    }
                }
            }
    ) {
        content()
        // `key` remounts the burst on every double-tap so the one-shot
        // animation replays from the start.
        key(burstKey.value) {
            if (burstKey.value > 0) HeartBurst(heartSizePx)
        }
    }
}

/** Maps the tapped element to its image-tile index: tiles are the grid's
 *  `background-image` divs, whose DOM order matches the post's url order. */
private fun resolveTileIndex(container: Element, target: Element?): Int? {
    val tiles = container.querySelectorAll("div[style*='background-image']")
    var t: Element? = target
    while (t != null && t !== container) {
        for (i in 0 until tiles.length) {
            if (tiles.item(i) === t) return i
        }
        t = t.parentElement
    }
    return null
}

// Red heart that pops over the photo on double-tap. Driven by the Web Animations
// API on the element ref (no global keyframes), so each remount replays it
// cleanly. The silhouette is a mask recolor (same trick as the create-post
// MiniCell) so it stays a solid red on any photo.
@Composable
private fun HeartBurst(sizePx: Int) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("pointer-events", "none")
                property("z-index", "2")
            }
    ) {
        Box(
            modifier = Modifier
                .attrsModifier {
                    ref { el ->
                        el.asDynamic().animate(
                            arrayOf(
                                js("({ opacity: 0, transform: 'scale(0.2)' })"),
                                js("({ opacity: 1, transform: 'scale(1.15)', offset: 0.2 })"),
                                js("({ opacity: 1, transform: 'scale(0.95)', offset: 0.35 })"),
                                js("({ opacity: 1, transform: 'scale(1)', offset: 0.5 })"),
                                js("({ opacity: 1, transform: 'scale(1)', offset: 0.8 })"),
                                js("({ opacity: 0, transform: 'scale(1)' })")
                            ),
                            js("({ duration: 1000, easing: 'ease-out' })")
                        )
                        onDispose {}
                    }
                }
                .styleModifier {
                    property("width", "${sizePx}px")
                    property("height", "${sizePx}px")
                    property("background-color", BURST_RED)
                    val mask = "url('${AppIcons.Heart}') no-repeat center / contain"
                    property("mask", mask)
                    property("-webkit-mask", mask)
                    property("filter", "drop-shadow(0 2px 12px rgba(0,0,0,0.45))")
                    property("opacity", "0")
                    property("transform", "scale(0.2)")
                    property("will-change", "transform, opacity")
                    property("pointer-events", "none")
                }
        )
    }
}
